import React, { useMemo, useState } from 'react';
import { Container, Card, Button, Alert, Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import RazaoSocialService from '~/Services/RazaoSocialService';
import FiltrosEscopo from './components/FiltrosEscopo';
import FiltrosStatus from './components/FiltrosStatus';
import FiltrosSocios from './components/FiltrosSocios';
import SeletorRazoes from './components/SeletorRazoes';
import OpcoesImpressao from './components/OpcoesImpressao';
import TabelaRazoes from './components/TabelaRazoes';
import BotaoExportarExcel from './components/BotaoExportarExcel';

// W-0779 — Relatório Razão Social (Imob > Relatórios > Contábil). READ-ONLY.
// Fonte: UListaRazao.pas (TFListaRazao) + UQRListaRazao/UQRListaRazaoSimples.
// Fluxo: filtros -> Buscar (lista razões) -> selecionar razões + opções -> Gerar -> tabela + Excel.

// LICOES §1.6: schema completo (required + min + ref). codigosRazao.min(1); GE×GrupoGE exclusivos.
const schemaGeracao = Yup.object().shape({
  codigosRazao: Yup.array()
    .of(Yup.number())
    .min(1, 'Selecione ao menos uma razão')
    .required('Selecione ao menos uma razão'),
});

const schemaEscopo = Yup.object().shape({
  codigoGrupoEmpresa: Yup.number()
    .nullable()
    .when('codigoGrupoGE', {
      is: (val) => val != null,
      then: (s) =>
        s.test(
          'exclusivo',
          'GE e GrupoGE são mutuamente exclusivos',
          (v) => v == null
        ),
    }),
  codigoGrupoGE: Yup.number().nullable(),
});

const STATUS_INICIAL = {
  incluirAtiva: true,
  incluirEncerrada: true,
  incluirPreAtiva: true,
  incluirGerencial: true,
};

const OPCOES_INICIAL = {
  // Defaults dos 4 CheckBoxes = TODOS desmarcados (REPLICA-DELPHI — q-A-W0779-003).
  incluirAdministradores: false,
  incluirResponsavel: false,
  incluirSociosAtuais: false,
  incluirContaCapital: false,
  tipoRelatorio: 'analitico',
  ordem: 'razao_social',
};

const mapTipoParticipacao = (valor) => {
  // Backend enum: Cem=0, ComOutros=1, Sem=2 (TipoParticipacao).
  if (valor === '100') return 0;
  if (valor === 'com_outros') return 1;
  if (valor === 'sem') return 2;
  return null;
};

const RazaoSocial = () => {
  const [escopo, setEscopo] = useState({ codigoGrupoEmpresa: null, codigoGrupoGE: null });
  const [status, setStatus] = useState(STATUS_INICIAL);
  const [socios, setSocios] = useState([]);
  const [tipoParticipacao, setTipoParticipacao] = useState('');
  const [cnpjFiltros, setCnpjFiltros] = useState({
    apenasComCnpj: false,
    apenasSemCnpj: false,
    apenasSemRazaoSocial: false,
    apenasSemNumOrgao: false,
  });
  const [disponiveis, setDisponiveis] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [opcoes, setOpcoes] = useState(OPCOES_INICIAL);
  const [resultado, setResultado] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState('');

  const handleBuscar = async () => {
    setErro('');
    try {
      await schemaEscopo.validate(escopo);
    } catch (validationErr) {
      setErro(validationErr.message);
      toast.warn(validationErr.message);
      return;
    }
    setBuscando(true);
    setResultado(null);
    try {
      const filtros = {
        codigo_grupo_empresa: escopo.codigoGrupoEmpresa,
        codigo_grupo_ge: escopo.codigoGrupoGE,
        incluir_ativa: status.incluirAtiva,
        incluir_encerrada: status.incluirEncerrada,
        incluir_pre_ativa: status.incluirPreAtiva,
        incluir_gerencial: status.incluirGerencial,
        apenas_com_cnpj: cnpjFiltros.apenasComCnpj,
        apenas_sem_cnpj: cnpjFiltros.apenasSemCnpj,
        apenas_sem_razao_social: cnpjFiltros.apenasSemRazaoSocial,
        apenas_sem_num_orgao: cnpjFiltros.apenasSemNumOrgao,
        filtro_socios: socios.map((s) => ({ nome: s.nome, cpf: s.cpf })),
        tipo_participacao: socios.length > 0 ? mapTipoParticipacao(tipoParticipacao) : null,
      };
      const res = await RazaoSocialService.buscarRazoes(filtros);
      const lista = Array.isArray(res) ? res : [];
      setDisponiveis(lista);
      setSelecionadas([]);
      if (lista.length === 0) {
        setErro('Nenhuma razão social encontrada.');
      }
    } catch (err) {
      const msg = (err && err.message) || 'Não foi possível carregar as razões sociais.';
      setErro(msg);
      toast.error(msg);
    } finally {
      setBuscando(false);
    }
  };

  const payloadGeracao = useMemo(
    () => ({
      codigos_razao: selecionadas.map((r) => r.codigo),
      incluir_responsavel: opcoes.incluirResponsavel,
      incluir_administradores: opcoes.incluirAdministradores,
      incluir_socios_atuais: opcoes.incluirSociosAtuais,
      incluir_conta_capital: opcoes.incluirContaCapital,
      tipo_relatorio: opcoes.tipoRelatorio === 'sintetico' ? 1 : 0,
      ordem: opcoes.ordem === 'ge_e_razao_social' ? 1 : 0,
    }),
    [selecionadas, opcoes]
  );

  const handleGerar = async () => {
    setErro('');
    try {
      await schemaGeracao.validate({ codigosRazao: selecionadas.map((r) => r.codigo) });
    } catch (validationErr) {
      setErro(validationErr.message);
      toast.warn(validationErr.message);
      return;
    }
    setGerando(true);
    setResultado(null);
    try {
      const res = await RazaoSocialService.gerarRelatorio(payloadGeracao);
      setResultado(res);
    } catch (err) {
      const msg = (err && err.message) || 'Não foi possível gerar o relatório.';
      setErro(msg);
      toast.error(msg);
    } finally {
      setGerando(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <Header title="Relatório Razão Social" />

      <Card className="mt-3">
        <Card.Body>
          <FiltrosEscopo
            codigoGrupoEmpresa={escopo.codigoGrupoEmpresa}
            codigoGrupoGE={escopo.codigoGrupoGE}
            onChange={setEscopo}
          />
          <FiltrosStatus valores={status} onChange={setStatus} />
          <Row className="mb-3">
            <Col xs={12}>
              <Form.Label className="small mb-1 d-block">Filtros de CNPJ / Razão / Órgão</Form.Label>
              <div className="d-flex flex-wrap">
                <Form.Check
                  type="checkbox"
                  id="cnpj-apenas-com"
                  className="mr-4"
                  label="Apenas com CNPJ"
                  checked={cnpjFiltros.apenasComCnpj}
                  onChange={(e) =>
                    setCnpjFiltros({ ...cnpjFiltros, apenasComCnpj: e.target.checked })
                  }
                />
                <Form.Check
                  type="checkbox"
                  id="cnpj-apenas-sem"
                  className="mr-4"
                  label="Apenas sem CNPJ"
                  checked={cnpjFiltros.apenasSemCnpj}
                  onChange={(e) =>
                    setCnpjFiltros({ ...cnpjFiltros, apenasSemCnpj: e.target.checked })
                  }
                />
                <Form.Check
                  type="checkbox"
                  id="cnpj-sem-razao"
                  className="mr-4"
                  label="Apenas sem Razão Social"
                  checked={cnpjFiltros.apenasSemRazaoSocial}
                  onChange={(e) =>
                    setCnpjFiltros({ ...cnpjFiltros, apenasSemRazaoSocial: e.target.checked })
                  }
                />
                <Form.Check
                  type="checkbox"
                  id="cnpj-sem-orgao"
                  className="mr-4"
                  label="Apenas sem Nº Órgão"
                  checked={cnpjFiltros.apenasSemNumOrgao}
                  onChange={(e) =>
                    setCnpjFiltros({ ...cnpjFiltros, apenasSemNumOrgao: e.target.checked })
                  }
                />
              </div>
            </Col>
          </Row>
          <FiltrosSocios
            socios={socios}
            tipoParticipacao={tipoParticipacao}
            onChangeSocios={setSocios}
            onChangeTipoParticipacao={setTipoParticipacao}
          />
          <Button variant="primary" onClick={handleBuscar} disabled={buscando}>
            {buscando ? 'Buscando...' : 'Buscar Razões'}
          </Button>
        </Card.Body>
      </Card>

      {buscando && <Loading label="Buscando razões sociais..." />}

      {!buscando && disponiveis.length > 0 && (
        <Card className="mt-3">
          <Card.Body>
            <SeletorRazoes
              disponiveis={disponiveis}
              selecionadas={selecionadas}
              onChangeSelecionadas={setSelecionadas}
            />
            <OpcoesImpressao valores={opcoes} onChange={setOpcoes} />
            <div className="d-flex">
              <Button
                variant="primary"
                className="mr-2"
                onClick={handleGerar}
                disabled={gerando || selecionadas.length === 0}
              >
                {gerando ? 'Gerando...' : 'Gerar Relatório'}
              </Button>
              <BotaoExportarExcel
                payload={payloadGeracao}
                disabled={gerando || selecionadas.length === 0}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {gerando && <Loading label="Gerando relatório..." />}

      {!buscando && !gerando && erro && (
        <Alert variant="warning" className="mt-3">
          {erro}
        </Alert>
      )}

      {!gerando && resultado && <TabelaRazoes resultado={resultado} />}
    </Container>
  );
};

export default RazaoSocial;
